<script setup>
import {getSectorFileName} from '~/globals/globals.js'
import {gsap} from 'gsap'
import ScrollToPlugin from 'gsap/ScrollToPlugin'
import {useLocalePath} from 'vue-i18n-routing'
import {useMediahubStore} from '~/store/mediahubStore'

const mediahubStore = useMediahubStore()

const router = useRouter()

const localePath = useLocalePath()
const {locale} = useI18n()

gsap.registerPlugin(ScrollToPlugin)

const props = defineProps({
  color: {
    type: String,
    default: 'dark',
    validator(value) {
      return ['dark', 'light'].includes(value)
    },
  },
  content: {
    type: Object,
    required: true,
  },
  goBack: {
    type: Boolean,
    default: false,
  },
  internalNav: {
    type: Object,
    required: false,
  },
  internalNavCurrentPath: {
    type: String,
    required: false,
  },
  layout: {
    type: Number,
    default: 1,
    validator(value) {
      return value >= 0 && value <= 2
    },
  },
  logout: {
    type: Boolean,
    default: false,
  },
  pattern: {
    type: Boolean,
    default: false,
  },
})

// get sectors and resources for internal navigation
const section = reactive(props.content.section)
const selectedSector = ref()

function scrollDiscoverMore(event) {
  const scrollTarget = event.target.dataset.scroll

  gsap.to(window, {duration: 0.6, scrollTo: scrollTarget, ease: 'power2.inOut'})
}

// get route parameters
const route = useRoute()

// get complementary theme color
function complementaryColor(color) {
  if (color === 'dark') return 'light'
  else return 'dark'
}

const generateSlug = SlugifyUtils()
let slug = ''

// selected sector label for mobile
if (props.internalNav) {
  props.internalNav.menu_items.forEach((item) => {
    // sectors page
    if (route.params.sectorSlug) {
      slug = route.params.sectorSlug

      if (item.path == '/sectors/' + slug) {
        selectedSector.value = item.label
      }
    } // services page
    else if (route.params.serviceSlug) {
      slug = route.params.serviceSlug

      if (item.path == '/services/' + slug) {
        selectedSector.value = item.label
      }
    } // country pages
    else if (route.params.countrySlug) {
      slug = route.params.countrySlug

      if (item.path == '/contacts/' + slug) {
        selectedSector.value = item.label
      }
    } // other pages
    else if (props.internalNavCurrentPath) {
      if (item.path == props.internalNavCurrentPath) {
        selectedSector.value = item.label
      }
    }
  })
}

const checkActiveNav = (item) => {
  // sectors page
  if (route.params.sectorSlug) {
    const slugTmp = route.params.sectorSlug

    if (item.path == '/sectors/' + slugTmp) {
      return true
    }
  } // services page
  else if (route.params.serviceSlug) {
    const slugTmp = route.params.serviceSlug

    if (item.path == '/services/' + slugTmp) {
      return true
    }
  } // country page
  else if (route.params.countrySlug) {
    const slugTmp = route.params.countrySlug

    if (item.path == '/contacts/' + slugTmp) {
      return true
    }
  } // investor relations page
  else if (route.params.investorSlug) {
    const slugTmp = route.params.investorSlug

    if (item.path == '/investor-relations/' + slugTmp) {
      return true
    }
  } // other pages
  else if (props.internalNavCurrentPath) {
    if (item.path == props.internalNavCurrentPath) {
      return true
    }
  }

  return false
}

const heroInternalNavList = ref()
const heroInternalNavlabelMobile = ref()

function toggleInternalNavigationSubmenu(event) {
  heroInternalNavlabelMobile.value.classList.toggle('open')
  heroInternalNavList.value.classList.toggle('open')
}

const logout = () => {
  mediahubStore.logOut()

  // Redirect
  if (locale.value === 'en') router.replace('/media-hub/')
  else router.replace('/' + locale.value + '/media-hub/')
}

// patternname generation
const patternName = ref('')
if (route.params && route.params.sectorSlug) {
  patternName.value = getSectorFileName(route.params.sectorSlug, locale.value)
}
</script>

<!-- Layout 0 - empty with actions (ex. sector portfolio)-->
<!-- Layout 1 - blue or img background horizontal hero (ex. blue: about pages, approach pages - ex. img: sectors, services )-->
<!-- Layout 2 - image background vertical hero (ex. project detail, news detail)-->

<template>
  <transition>
    <section
      class="hero hero--general"
      :class="['layout--' + props.layout, 'hero--' + props.color]">
      <video
        class="hero-video"
        autoplay
        muted
        loop
        playsinline
        preload
        v-if="section.video">
        <source
          :src="section.video.url"
          :type="getVideoType(section.video.url)" />
        <meta itemprop="name" content="Pininfarina" />
      </video>
      <div
        class="hero-bgs animate-in-fade animate-out-fade"
        v-if="!section.video && section.assets">
        <picture class="hero-image">
          <source
            v-for="(asset, index) in filterPictureSourceTags(section.assets)"
            :key="index"
            :srcset="asset.src"
            :type="asset.format"
            :media="asset.media" />
          <img
            v-for="(asset, index) in filterPictureImageTag(section.assets)"
            :key="index"
            :srcset="asset.src"
            :alt="section.title + ' - Pininfarina'" />
        </picture>
        <div class="hero-layer" v-if="section.assets"></div>
      </div>
      <!-- <div class="hero-layer" v-if="section.assets"></div> -->
      <SharedPattern
        class="hero-pattern"
        :sector="patternName"
        v-if="props.pattern && patternName" />
      <div class="hero-content">
        <div
          class="hero-content-nav animate-in-stagger-bottom animate-out-right animate-once"
          v-if="props.goBack || props.logout || props.internalNav">
          <SharedBasicCta
            class="animate-stagger-item"
            :label="$t('go_back')"
            :direction="'left'"
            :position="'left'"
            :url="''"
            @click.prevent="$router.back()"
            :color="complementaryColor(props.color)"
            v-if="props.goBack" />
          <SharedBasicCta
            class="animate-stagger-item"
            :label="$t('logout')"
            :direction="'left'"
            :position="'left'"
            :url="''"
            @click.prevent="logout"
            :color="complementaryColor(props.color)"
            v-if="props.logout" />
          <nav class="hero-internal-nav" v-if="props.internalNav">
            <div class="hero-internal-nav-label animate-stagger-item">
              {{ props.internalNav.label }}
            </div>
            <div
              class="hero-internal-nav-label-mobile"
              ref="heroInternalNavlabelMobile"
              @click="toggleInternalNavigationSubmenu">
              {{ selectedSector }}
              <svg class="icon icon--btn-round-small icon--white-stroke">
                <use xlink:href="#icon-chevron"></use>
              </svg>
            </div>
            <ul class="hero-internal-nav-list" ref="heroInternalNavList">
              <li
                class="hero-internal-nav-item animate-stagger-item"
                v-for="(item, index) in props.internalNav.menu_items" :key="index">
                <NuxtLink
                  :to="localePath(item.path)"
                  class="hero-internal-nav-link sensible"
                  :class="{
                    active: checkActiveNav(item),
                  }"
                  >{{ item.label }}</NuxtLink
                >
              </li>
            </ul>
          </nav>
        </div>
        <div
          class="hero-date animate-in-bottom animate-out-left"
          v-if="props.layout == 2 && props.content.section.date">
          {{ useDateFormatter(props.content.section.date, locale) }}
        </div>
        <div class="hero-content-top">
          <h1
            class="hero-title hero-title--explain animate-in-stagger-left animate-out-left animate-once"
            v-if="props.layout == 0 && route.params.sectorSlug">
            <div class="hero-title-main animate-stagger-item">
              {{ $t('portfolio') }}
            </div>
            <sup class="hero-title-superscript animate-stagger-item">
              {{ props.content.section.title }}
            </sup>
          </h1>
          <h1
            class="hero-title hero-title--explain animate-in-bottom animate-out-left"
            v-if="props.layout == 0 && !route.params.sectorSlug">
            <div class="hero-title-main">
              {{ props.content.section.title }}
            </div>
          </h1>
          <h1
            class="hero-title animate-in-bottom animate-out-left"
            v-if="props.layout == 1 || props.layout == 2">
            {{ props.content.section.title }}
          </h1>
          <div
            class="hero-subtitle animate-in-bottom animate-out-right"
            v-html="props.content.section.description"
            v-if="props.layout == 1 && props.content.section.description"></div>
        </div>
        <div
          class="hero-content-bottom"
          v-if="props.layout == 1 || props.layout == 2">
          <div
            class="hero-subtitle animate-in-bottom animate-out-right"
            v-html="props.content.section.description"
            v-if="props.layout == 2 && props.content.section.description"></div>
          <div
            data-scroll="#content"
            class="cta cta--light sensible animate-in-fade-early animate-out-left"
            @click="scrollDiscoverMore">
            {{ $t('discover_more') }}
            <div class="menu-link-btn btn btn--round-small btn--round-light">
              <svg class="icon icon--btn-round-small icon--rotate-90">
                <use xlink:href="#icon-arrow"></use>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </section>
  </transition>
</template>

<style lang="scss" scoped>
.hero--general {
  background-color: $color-blue;
  display: flex;
  min-height: 85vh;
  padding-bottom: pxrem(60);
  position: relative;
  z-index: 0;

  &::before {
    background: linear-gradient(0deg, transparent 0%, $color-blue 100%);
    content: '';
    display: block;
    height: 12vh;
    position: absolute;
    top: 0;
    width: 100%;
    z-index: 1;
  }

  &.print-version {
    padding-bottom: pxrem(30);

    &::before {
      display: none;
    }

    .hero-content {
      margin: 0 !important;
      padding-top: pxrem(30);
    }

    .hero-title {
      font-size: pxrem(36);
      margin-bottom: pxrem(20);
    }
  }

  .hero-image {
    bottom: 0;
    left: 0;
    object-fit: cover;
    position: absolute;
    right: 0;
    top: 0;

    img {
      height: 100%;
      object-fit: cover;
      width: 100%;
    }
  }

  .hero-video {
    bottom: 0;
    left: 0;
    object-fit: cover;
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    z-index: -1;

    @media (min-aspect-ratio: 16/9) {
      height: 85vh;
      width: 100%;
    }

    @media (max-aspect-ratio: 16/9) {
      height: 100%;
      width: auto;
    }
  }

  .hero-layer {
    background-color: rgba(darken($color-blue, 5%), 0.4);
    bottom: 0;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
  }

  .hero-pattern {
    bottom: 0;
    left: 0;
    position: absolute;
    right: 0;
  }

  .hero-content {
    @include make-col-ready();
    display: flex;
    flex-direction: column;
    padding-top: pxrem(120);
    position: relative;
    z-index: 1;

    @include mq(md) {
      @include make-col(22);
      @include make-col-offset(1);
      padding-top: pxrem(145);
    }
  }

  .hero-content-nav {
    border-bottom: 1px solid $color-gray-blue;
    margin-bottom: pxrem(35);
    padding-bottom: pxrem(20);

    @include mq(md) {
      margin-bottom: pxrem(60);
    }
  }

  .hero-internal-nav {
    @include mq(md) {
      display: flex;
    }

    .hero-internal-nav-label {
      display: none;

      @include mq(md) {
        @include make-col(4);
        @include paragraph($color-white);
        display: block;
      }
    }

    .hero-internal-nav-label-mobile {
      @include label($color-white);
      display: flex;
      justify-content: space-between;

      svg {
        margin-left: pxrem(10);
        transition: transform $transition-default;
      }

      @include mq(md) {
        display: none;
      }

      &.open {
        svg {
          transform: rotateX(180deg);
        }
      }
    }

    .hero-internal-nav-list {
      @include list-reset;
      max-height: 0;
      overflow: hidden;

      transition: max-height $transition-default,
        padding-top $transition-default;
      will-change: max-height;

      @include mq(md) {
        @include make-col(17);
        @include make-col-offset(1);
        max-height: 100%;
        padding-top: 0;
      }

      &.open {
        max-height: 1000px;
        padding-top: pxrem(10);
      }
    }

    .hero-internal-nav-item {
      @include mq(md) {
        display: inline-block;
        margin-right: pxrem(15);
      }

      &:last-of-type {
        margin-right: 0;
      }
    }

    .hero-internal-nav-link {
      @include paragraph($color-white);
      display: block;
      padding-bottom: pxrem(4);
      padding-top: pxrem(4);
      text-decoration: none;
      transition: opacity $transition-default;

      &.active {
        display: none;
      }

      @include mq(md) {
        opacity: 0.3;
        padding-bottom: 0;
        padding-top: 0;

        &:hover,
        &.active {
          display: block;
          opacity: 1;
        }
      }
    }
  }

  .hero-content-top {
    @include mq(md) {
      display: flex;
      justify-content: space-between;
    }

    .hero-subtitle {
      @include display-3($color-white);

      @include mq(md) {
        @include make-col(9);
        @include make-col-offset(3);
        margin-top: pxrem(20);
      }

      p:first-of-type {
        margin-top: 0;
      }
    }
  }

  .hero-date {
    @include display-3($color-white);
    margin-bottom: pxrem(20);

    @include mq(md) {
      margin-bottom: pxrem(30);
    }
  }

  .hero-title {
    @include display-1($color-white);
    font-weight: 700;
    margin-bottom: pxrem(20);
    user-select: none;

    @include mq(md) {
      @include make-col(10);
      margin-bottom: 0;
    }
  }

  .hero-content-bottom {
    margin-top: auto;

    .hero-subtitle {
      @include display-3($color-white);
      margin-bottom: pxrem(30);

      @include mq(md) {
        @include make-col(10);
        margin-bottom: pxrem(45);
      }

      p:first-of-type {
        margin-top: 0;
      }
    }
  }

  &.layout--0 {
    height: auto;
    min-height: auto;

    .hero-title--explain {
      .hero-title-main {
        display: inline-block;
        margin-right: pxrem(5);
        text-transform: capitalize;
      }

      .hero-title-superscript {
        display: inline-block;
        font-weight: 400;
        top: 0;

        @include mq(md) {
          top: -0.5em;
        }
      }
    }
  }

  &.layout--2 {
    min-height: 100vh;
  }

  &.hero--light {
    background-color: $color-white;

    .hero-title {
      color: $color-blue;

      @include mq(md) {
        flex: 100%;
        max-width: 100%;
      }
    }
  }
}
</style>
